import { useSyncExternalStore } from "react";

/**
 * Product-analytics consent, one record per account. Analytics is on unless
 * the account turns it off in Preferences; every analytics tracker runs only
 * while `allowed` is true. Essential processing (sign-in, settings, Sentry
 * error reporting) does not ask.
 *
 * Bridge: records live in this browser until the account record on the
 * server replaces them, so a choice does not yet follow the user to another
 * browser. Storing the choice is strictly necessary to honour it.
 */
export type TrackingChoice = "granted" | "denied";

export type TrackingConsent = {
  /** The saved choice; otherwise on. */
  allowed: boolean;
  /** Absent until the user uses the toggle. */
  choice?: TrackingChoice;
};

type ConsentRecord = { choice?: TrackingChoice; decidedAt?: string };

const STORAGE_KEY = "Shuttlerock:TrackingConsent";

const listeners = new Set<() => void>();
// Records whose save failed win over storage, so a choice still applies for
// this page when storage is unavailable or full.
const unsavedRecords = new Map<string, ConsentRecord>();
const snapshots = new Map<string, TrackingConsent>();

/** Undefined when storage cannot be read. */
function readRecords(): Record<string, ConsentRecord> | undefined {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : undefined;
    return parsed && typeof parsed === "object" ? (parsed as Record<string, ConsentRecord>) : {};
  } catch {
    return undefined;
  }
}

function writeRecord(principalKey: string, record: ConsentRecord) {
  try {
    const records = { ...readRecords(), [principalKey]: record };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(records));
    unsavedRecords.delete(principalKey);
  } catch {
    unsavedRecords.set(principalKey, record);
  }
}

function choiceFor(principalKey: string): TrackingChoice | undefined {
  const { choice } = unsavedRecords.get(principalKey) ?? readRecords()?.[principalKey] ?? {};
  return choice === "granted" || choice === "denied" ? choice : undefined;
}

/** Consent for a signed-in account; with no account there is no choice, only the default. */
export function getTrackingConsent(principalKey?: string): TrackingConsent {
  const choice = principalKey ? choiceFor(principalKey) : undefined;
  // useSyncExternalStore needs the same object while nothing has changed.
  const cacheKey = choice ?? "default";
  const cached = snapshots.get(cacheKey);
  if (cached) return cached;
  const consent: TrackingConsent = choice
    ? { allowed: choice === "granted", choice }
    : { allowed: true };
  snapshots.set(cacheKey, consent);
  return consent;
}

/** Saves the account's choice from the Preferences toggle. */
export function setTrackingConsent(principalKey: string, allowed: boolean) {
  writeRecord(principalKey, {
    choice: allowed ? "granted" : "denied",
    decidedAt: new Date().toISOString(),
  });
  notify();
}

function notify() {
  for (const listener of listeners) listener();
}

function onStorage(event: StorageEvent) {
  if (event.key === STORAGE_KEY || event.key === null) notify();
}

/** Calls `listener` whenever consent may have changed, including from another tab. */
export function subscribeTrackingConsent(listener: () => void) {
  listeners.add(listener);
  if (listeners.size === 1) window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) window.removeEventListener("storage", onStorage);
  };
}

export function useTrackingConsent(principalKey?: string): TrackingConsent {
  return useSyncExternalStore(subscribeTrackingConsent, () => getTrackingConsent(principalKey));
}
